import React, { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import useStyles from '../../theme/useStyles';
import { useMemberDirectory, useChurchConfig, useSearch, usePagination, useNotification } from '../../hooks';
import {
  PageHeader, SearchBar, Pagination, Badge, TablePanel, Notification
} from '../../components/ui';
import PrintButton from '../../components/ui/PrintButton';
import type { Column } from '../../components/ui';
import { formatDate, ageFrom } from '../../utils/format';
import type { Member } from '../../types';

// One table for all four sacraments.
//
// The registers record what happened; this answers the question a parish
// actually asks before a confirmation class or a visit — who has not reached
// the next stage yet. Every column is read straight off the member record,
// which the registers now keep up to date, so there is no join and no second
// source of truth to drift.
//
// It reads the whole light member list rather than a server page, because the
// due filters have to be applied across everyone, not across one page of rows.
// That list is the same projection the directory already loads.

type FilterKey = 'all' | 'baptism' | 'communion' | 'confirmation' | 'complete' | 'departed';

/** Fallbacks when the church configuration has never been saved. */
const DEFAULT_BAPTISM_DUE_MONTHS = 12;
const DEFAULT_COMMUNION_DUE_AGE = 13;
const DEFAULT_CONFIRMATION_DUE_AGE = 13;

const MONTHS_PER_YEAR = 12;

/** Whole months from an ISO date to today, or null when it is missing/malformed. */
const monthsSince = (iso?: string): number | null => {
  if (!iso) return null;
  const from = new Date(iso);
  if (Number.isNaN(from.getTime())) return null;
  const now = new Date();
  let months = (now.getFullYear() - from.getFullYear()) * MONTHS_PER_YEAR
    + (now.getMonth() - from.getMonth());
  if (now.getDate() < from.getDate()) months -= 1;
  return months;
};

const has = (value?: string): boolean => Boolean(value && value.trim());

interface Thresholds {
  baptismDueMonths: number;
  communionDueAge: number;
  confirmationDueAge: number;
}

interface SacramentRow extends Member {
  ageMonths: number | null;
  baptismDue: boolean;
  communionDue: boolean;
  confirmationDue: boolean;
  complete: boolean;
}

/**
 * A stage is due once the member is old enough and has no record of it. Age
 * unknown means never due — nagging about a member whose date of birth was
 * never captured would put a permanent warning on the list nobody can clear.
 */
const describe = (member: Member, limits: Thresholds): SacramentRow => {
  const ageMonths = monthsSince(member.dateOfBirth);
  const baptised = has(member.baptismDate);
  const communed = has(member.communionDate) || member.communionStatus === 'Received';
  const confirmed = has(member.confirmationDate);

  const oldEnough = (years: number) =>
    ageMonths !== null && ageMonths >= years * MONTHS_PER_YEAR;

  // Nothing is due of someone who has died. They are excluded from the working
  // list anyway, but they still appear under Departed, and a "⚠️ Due" badge
  // against a dead member reads as a reproach nobody can act on.
  const departed = member.status === 'Deceased';

  return {
    ...member,
    ageMonths,
    baptismDue: !departed && !baptised && ageMonths !== null && ageMonths >= limits.baptismDueMonths,
    communionDue: !departed && !communed && oldEnough(limits.communionDueAge),
    confirmationDue: !departed && !confirmed && oldEnough(limits.confirmationDueAge),
    complete: baptised && communed && confirmed
  };
};

/** ✅ with the date, ⚠️ DUE, or a quiet dash. */
const Stage = ({ date, label, due, onClick }: {
  date?: string;
  label?: ReactNode;
  due?: boolean;
  onClick?: () => void;
}) => {
  const content = has(date)
    ? <Badge tone="success">✅ {formatDate(date)}</Badge>
    : due
      ? <Badge tone="warning">⚠️ Due</Badge>
      : label
        ? <Badge tone="neutral">{label}</Badge>
        : <span style={{ opacity: 0.5 }}>—</span>;

  return (
    <span
      onClick={(e) => { e.stopPropagation(); onClick?.(); }}
      style={{ cursor: onClick ? 'pointer' : 'default' }}
      title={onClick ? 'Open in the register' : undefined}
    >
      {content}
    </span>
  );
};

const FILTERS: Array<{ key: FilterKey; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'baptism', label: 'Baptism due' },
  { key: 'communion', label: 'Communion due' },
  { key: 'confirmation', label: 'Confirmation due' },
  { key: 'complete', label: 'Complete' },
  // Kept out of the other five: someone who has died is not waiting on
  // anything, and leaving them in would inflate every "due" count for ever.
  { key: 'departed', label: '⚰️ Departed' }
];

export interface SacramentStatusProps {
  isDarkMode?: boolean;
  /** The Families / Members / Sacraments switch, rendered in place of a title. */
  headerSwitch?: ReactNode;
}

const SacramentStatus = ({ isDarkMode, headerSwitch }: SacramentStatusProps) => {
  const s = useStyles(isDarkMode);
  const t = s.t;
  const navigate = useNavigate();
  // Printing is the one action here that can fail, so it needs somewhere to say so.
  const { notification, notify, dismiss } = useNotification();
  const { members, loading } = useMemberDirectory();
  const config = useChurchConfig();

  const [filter, setFilter] = useState<FilterKey>('all');

  const limits: Thresholds = {
    baptismDueMonths: Number(config.baptismDueMonths) || DEFAULT_BAPTISM_DUE_MONTHS,
    communionDueAge: Number(config.communionDueAge) || DEFAULT_COMMUNION_DUE_AGE,
    confirmationDueAge: Number(config.confirmationDueAge) || DEFAULT_CONFIRMATION_DUE_AGE
  };

  // The directory tab's population: a member who has died or moved away is not
  // waiting for anything.
  const rows = useMemo(
    () => members
      .filter((m) => m.status !== 'Deceased' && m.status !== 'Transferred' && m.status !== 'Inactive')
      .map((m) => describe(m, limits)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [members, limits.baptismDueMonths, limits.communionDueAge, limits.confirmationDueAge]
  );

  /** Members the burial register has closed out. Their own filter, their own list. */
  const departed = useMemo(
    () => members.filter((m) => m.status === 'Deceased').map((m) => describe(m, limits)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [members, limits.baptismDueMonths, limits.communionDueAge, limits.confirmationDueAge]
  );

  const counts = useMemo(() => ({
    all: rows.length,
    baptism: rows.filter((r) => r.baptismDue).length,
    communion: rows.filter((r) => r.communionDue).length,
    confirmation: rows.filter((r) => r.confirmationDue).length,
    complete: rows.filter((r) => r.complete).length,
    departed: departed.length
  }), [rows, departed]);

  const inFilter = useMemo(() => {
    if (filter === 'departed') return departed;
    return rows.filter((r) => (
      filter === 'all' ? true
        : filter === 'baptism' ? r.baptismDue
        : filter === 'communion' ? r.communionDue
        : filter === 'confirmation' ? r.confirmationDue
        : r.complete
    ));
  }, [rows, departed, filter]);

  const { searchTerm, setSearchTerm, filtered, clear } =
    useSearch(inFilter, ['name', 'memberId', 'familyId', 'relativeName']);
  const page = usePagination(filtered);

  /** Opens the register on the right tab with this member already chosen. */
  const fileEntry = (route: string, type: string, form: Record<string, unknown>) =>
    navigate(`/${route}`, { state: { register: { type, form } } });

  /** Opens the register searched for this member, to reach their certificate. */
  const findEntry = (route: string, type: string, member: Member) =>
    navigate(`/${route}`, { state: { register: { type, search: member.name || member.memberId } } });

  const columns: Array<Column<SacramentRow>> = [
    {
      key: 'name',
      header: 'Member',
      render: (r) => (
        <>
          <strong>{r.name}</strong>
          <div style={{ fontSize: '11px', opacity: 0.7 }}>
            {[r.memberId, r.familyId].filter(Boolean).join('  ·  ')}
          </div>
        </>
      )
    },
    {
      key: 'age',
      header: 'Age',
      align: 'center',
      nowrap: true,
      render: (r) => (r.ageMonths === null ? '—' : ageFrom(r.dateOfBirth))
    },
    {
      key: 'baptism',
      header: '💧 Baptism',
      nowrap: true,
      render: (r) => (
        <Stage
          date={r.baptismDate}
          due={r.baptismDue}
          onClick={() => (has(r.baptismDate)
            ? findEntry('baptism', 'BAPTISM', r)
            : fileEntry('baptism', 'BAPTISM', { memberId: r.memberId, _mode: 'MEMBER' }))}
        />
      )
    },
    {
      key: 'communion',
      header: '🍞 Communion',
      nowrap: true,
      render: (r) => (
        <Stage
          date={r.communionDate}
          due={r.communionDue}
          // Before the register existed this was a hand-typed status, so a
          // member can be "Received" or "In Preparation" with no date.
          label={r.communionStatus && r.communionStatus !== 'Not Received' ? r.communionStatus : undefined}
          onClick={() => (has(r.communionDate)
            ? findEntry('baptism', 'COMMUNION', r)
            : fileEntry('baptism', 'COMMUNION', { memberId: r.memberId, personName: r.name }))}
        />
      )
    },
    {
      key: 'confirmation',
      header: '🕊️ Confirmation',
      nowrap: true,
      render: (r) => (
        <Stage
          date={r.confirmationDate}
          due={r.confirmationDue}
          onClick={() => (has(r.confirmationDate)
            ? findEntry('baptism', 'CONFIRMATION', r)
            : fileEntry('baptism', 'CONFIRMATION', { memberId: r.memberId, personName: r.name }))}
        />
      )
    },
    {
      key: 'burial',
      header: '⚰️ Burial',
      nowrap: true,
      // No green tick here. The other four columns celebrate a stage reached;
      // this one records a death, and it should not read like an achievement.
      render: (r) => {
        const content = has(r.dateOfDeath)
          ? <Badge tone="neutral">⚰️ {formatDate(r.dateOfDeath)}</Badge>
          : r.status === 'Deceased'
            ? <Badge tone="neutral">⚰️ Deceased</Badge>
            : <span style={{ opacity: 0.5 }}>—</span>;
        return (
          <span
            onClick={(e) => { e.stopPropagation(); navigate('/cemetery'); }}
            style={{ cursor: 'pointer' }}
            title="Open the cemetery register"
          >
            {content}
          </span>
        );
      }
    },
    {
      key: 'marriage',
      header: '💍 Marriage',
      nowrap: true,
      render: (r) => (
        <Stage
          date={r.dateOfMarriage}
          label={r.maritalStatus && r.maritalStatus !== 'Single' ? r.maritalStatus : undefined}
          onClick={() => (has(r.dateOfMarriage)
            ? findEntry('marriage', 'CERTIFICATE', r)
            : fileEntry('marriage', 'CERTIFICATE', r.gender === 'Female'
              ? { brideMemberId: r.memberId, brideName: r.name }
              : { groomMemberId: r.memberId, groomName: r.name }))}
        />
      )
    }
  ];

  return (
    <div style={s.container}>
      <Notification notification={notification} onClose={dismiss} />

      <PageHeader
        leading={headerSwitch}
        icon={headerSwitch ? undefined : '🕊️'}
        title={headerSwitch ? undefined : 'Sacraments'}
        isDarkMode={isDarkMode}
      />

      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: t.spacing.md }}>
        {FILTERS.map(({ key, label }) => {
          const on = filter === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => { setFilter(key); clear(); page.reset(); }}
              style={{
                padding: '8px 16px',
                border: `1px solid ${on ? t.primary : t.border}`,
                borderRadius: t.radius.pill,
                cursor: 'pointer',
                fontSize: t.fontSize.md,
                fontWeight: on ? t.fontWeight.bold : 'normal',
                backgroundColor: on ? t.primary : t.bgRaised,
                color: on ? '#FFF' : t.textMuted
              }}
            >
              {label} ({counts[key]})
            </button>
          );
        })}
      </div>

      <SearchBar
        value={searchTerm}
        onChange={(v) => { setSearchTerm(v); page.reset(); }}
        onClear={clear}
        placeholder="Search by name, member ID or family…"
        actions={(
          <PrintButton
            module="Members & Families"
            documentType="SACRAMENT_STATUS"
            size="sm"
            title="Sacrament-Status"
            data={() => ({
              title: 'Sacrament Status',
              reference: FILTERS.find((f) => f.key === filter)?.label || 'All',
              rowCount: page.currentItems.length,
              // The dates are stamped on the member; what is outstanding is
              // worked out here, so the sheet says it in words rather than
              // leaving the reader to compare three columns.
              rows: page.currentItems.map((r) => ({
                memberId: r.memberId || '',
                name: r.name || '',
                familyId: r.familyId || '',
                dateOfBirth: formatDate(r.dateOfBirth),
                age: r.ageMonths === null ? '' : ageFrom(r.dateOfBirth),
                baptism: has(r.baptismDate) ? formatDate(r.baptismDate) : 'Not recorded',
                communion: has(r.communionDate) ? formatDate(r.communionDate) : 'Not recorded',
                confirmation: has(r.confirmationDate) ? formatDate(r.confirmationDate) : 'Not recorded',
                outstanding: [
                  r.baptismDue && 'Baptism',
                  r.communionDue && 'Communion',
                  r.confirmationDue && 'Confirmation'
                ].filter(Boolean).join(', ') || (r.complete ? 'None' : '')
              }))
            })}
            onNotify={notify}
            isDarkMode={isDarkMode}
          />
        )}
      />

      <TablePanel
        title="Sacrament Status"
        below={<Pagination {...page} itemsOnPage={page.currentItems.length} label="members" />}
        columns={columns}
        rows={page.currentItems}
        loading={loading}
        rowKey={(r) => r.memberId || String(r.id)}
        emptyMessage={
          filter === 'all' ? 'No members found.'
            : filter === 'departed' ? 'No departed members recorded.'
            : 'Nobody is waiting on this one.'
        }
      />

      <p style={{ fontSize: t.fontSize.sm, color: t.textFaint, marginTop: t.spacing.md }}>
        A stage is marked <strong>Due</strong> once a member passes {limits.baptismDueMonths} months
        (baptism), age {limits.communionDueAge} (communion) or age {limits.confirmationDueAge} (confirmation)
        with nothing on file. Change those in Settings › Rules & Formats. Click any cell to open the register.
      </p>
    </div>
  );
};

export default SacramentStatus;
